import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, Calendar, Clock, Phone, Plus, Minus, CheckCircle2 } from "lucide-react";
import { getPost } from "@/data/blog";
import { COMPANY } from "@/data/content";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import StickyCallBar from "@/components/site/StickyCallBar";
import QuoteForm from "@/components/site/QuoteForm";

const SITE_URL = "https://honeybeeroofers.com";

function fmt(d) {
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function FaqBlock({ heading, items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="my-10">
      {heading && <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mb-5">{heading}</h2>}
      <div className="space-y-3">
        {items.map((f, i) => (
          <div key={i} className="border border-white/10 rounded-sm bg-carbon overflow-hidden" data-testid={`blog-faq-${i}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-white/[0.03] transition-colors">
              <span className="text-base font-semibold text-white">{f.q}</span>
              {open === i ? <Minus className="w-5 h-5 text-honey shrink-0" /> : <Plus className="w-5 h-5 text-honey shrink-0" />}
            </button>
            <AnimatePresence>
              {open === i && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                  <p className="px-5 pb-5 text-[15px] text-zinc-400 leading-relaxed">{f.a}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  const [modal, setModal] = useState(false);

  useEffect(() => {
    if (!post) return;
    document.title = post.metaTitle;
    window.scrollTo(0, 0);
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", post.metaDescription);
    setMeta("name", "keywords", post.keywords);
    setMeta("property", "og:title", post.metaTitle);
    setMeta("property", "og:description", post.metaDescription);
    setMeta("property", "og:type", "article");
    setMeta("property", "og:url", `${SITE_URL}/blog/${post.slug}`);

    let c = document.head.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
    c.setAttribute("href", `${SITE_URL}/blog/${post.slug}`);

    const faqBlock = post.content.find((b) => b.type === "faq");
    const articleLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.metaDescription,
      image: post.heroImage,
      datePublished: post.date,
      dateModified: post.date,
      author: { "@type": "Organization", name: post.author },
      publisher: { "@type": "Organization", name: COMPANY.name, logo: { "@type": "ImageObject", url: `${SITE_URL}/honeybee-logo.svg` } },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
    };
    const blocks = [articleLd];
    if (faqBlock) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqBlock.items.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      });
    }
    let script = document.getElementById("ld-json");
    if (!script) { script = document.createElement("script"); script.id = "ld-json"; script.type = "application/ld+json"; document.head.appendChild(script); }
    script.textContent = JSON.stringify(blocks);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="bg-obsidian min-h-screen">
      <Header onQuote={() => setModal(true)} />

      {/* Hero */}
      <section className="relative pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={post.heroImage} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-obsidian/90 to-obsidian" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8">
          <Link to="/blog" data-testid="back-blog" className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-honey mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> All posts
          </Link>
          <div className="flex items-center gap-4 text-xs text-zinc-300 mb-4">
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-honey" /> {fmt(post.date)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-honey" /> {post.readTime}</span>
          </div>
          <h1 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            {post.title}
          </h1>
          <p className="text-sm text-zinc-400 mt-4">By {post.author}</p>
        </div>
      </section>

      {/* Body */}
      <article className="pb-20 bg-obsidian">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {post.content.map((b, i) => {
            if (b.type === "h2") return <h2 key={i} className="text-2xl sm:text-3xl font-display font-bold text-white mt-12 mb-4">{b.text}</h2>;
            if (b.type === "p") return <p key={i} className="text-[17px] text-zinc-300 leading-[1.8] mb-5">{b.text}</p>;
            if (b.type === "ul") return (
              <ul key={i} className="space-y-3 my-6">
                {b.items.map((it, j) => (
                  <li key={j} className="flex items-start gap-3 text-[16px] text-zinc-300 leading-relaxed">
                    <CheckCircle2 className="w-5 h-5 text-honey mt-1 shrink-0" /><span>{it}</span>
                  </li>
                ))}
              </ul>
            );
            if (b.type === "faq") return <FaqBlock key={i} heading={b.heading} items={b.items} />;
            if (b.type === "cta") return (
              <div key={i} className="my-10 p-7 rounded-md border border-honey/30 bg-honey/5">
                <p className="text-lg text-white font-medium leading-relaxed">{b.text}</p>
                <div className="flex flex-col sm:flex-row gap-3 mt-5">
                  <button onClick={() => setModal(true)} data-testid="blog-cta-quote"
                    className="bg-honey text-white font-bold px-6 py-3.5 rounded-sm hover:bg-honey-600 transition-colors">
                    Get a Free Inspection
                  </button>
                  <a href={`tel:${COMPANY.phoneRaw}`} data-testid="blog-cta-call"
                    className="border border-white/20 text-white font-semibold px-6 py-3.5 rounded-sm hover:border-honey hover:text-honey transition-colors flex items-center justify-center gap-2">
                    <Phone className="w-4 h-4 text-honey" /> {COMPANY.phone}
                  </a>
                </div>
              </div>
            );
            return null;
          })}
        </div>
      </article>

      <Footer />
      <StickyCallBar onQuote={() => setModal(true)} />

      {modal && (
        <div data-testid="quote-modal" onClick={() => setModal(false)}
          data-lenis-prevent className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
            className="relative bg-carbon border border-white/10 rounded-md p-6 sm:p-8 w-full max-w-md my-8">
            <button onClick={() => setModal(false)} data-testid="quote-modal-close" className="absolute top-4 right-4 text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-display font-bold text-white mb-1">Free Roof Evaluation</h2>
            <p className="text-sm text-zinc-400 mb-5">Fixed, no-hassle pricing in 30 seconds.</p>
            <QuoteForm onSuccess={() => {}} />
          </motion.div>
        </div>
      )}
    </div>
  );
}
